import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ModeSelector } from './components/ModeSelector';
import { ProblemTypeSelector } from './components/ProblemTypeSelector';
import { ProblemDisplay } from './components/ProblemDisplay';
import { MultipleChoice } from './components/MultipleChoice';
import { FillInBlank } from './components/FillInBlank';
import { Timer } from './components/Timer';
import { ProgressBar } from './components/ProgressBar';
import { Results } from './components/Results';
import { FeedbackOverlay } from './components/FeedbackOverlay';
import { HighScores } from './components/HighScores';
import { Button } from './components/ui/Button';
import { X, ChevronLeft, Trophy } from 'lucide-react';
import { generateProblem } from './utils/problemGenerator';
import { GameSettings, GameStats, Problem, ProblemType } from './types';
type AppState = 'type_selection' | 'menu' | 'playing' | 'results' | 'high_scores';
export function App() {
  const [appState, setAppState] = useState<AppState>('type_selection');
  const [settings, setSettings] = useState<GameSettings | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<ProblemType[]>([]);
  const [currentProblem, setCurrentProblem] = useState<Problem | null>(null);
  const [stats, setStats] = useState<GameStats>({
    correct: 0,
    total: 0,
    streak: 0,
    bestStreak: 0,
    startTime: 0,
    history: []
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(false);
  const [lastCorrectAnswer, setLastCorrectAnswer] = useState<string | number>('');
  const handleTypeSelection = (types: ProblemType[]) => {
    setSelectedTypes(types);
    setAppState('menu');
  };
  const startGame = (partialSettings: Omit<GameSettings, 'problemTypes'>) => {
    const newSettings: GameSettings = {
      ...partialSettings,
      problemTypes: selectedTypes
    };
    setSettings(newSettings);
    setStats({
      correct: 0,
      total: 0,
      streak: 0,
      bestStreak: 0,
      startTime: Date.now(),
      history: []
    });
    setCurrentProblem(generateProblem(selectedTypes, newSettings.advancedOptions));
    setAppState('playing');
  };
  const handleAnswer = (answer: string) => {
    if (isTransitioning || !currentProblem || !settings) return;
    const isCorrect = Number(answer) === Number(currentProblem.answer);
    const newStreak = isCorrect ? stats.streak + 1 : 0;
    setLastAnswerCorrect(isCorrect);
    setLastCorrectAnswer(currentProblem.answer);
    setShowFeedback(true);
    setIsTransitioning(true);
    const newStats = {
      ...stats,
      correct: stats.correct + (isCorrect ? 1 : 0),
      total: stats.total + 1,
      streak: newStreak,
      bestStreak: Math.max(stats.bestStreak, newStreak),
      history: [...stats.history, {
        problem: currentProblem,
        userAnswer: answer,
        isCorrect,
        timeTaken: Date.now() - stats.startTime
      }]
    };
    setStats(newStats);
    if (settings.mode === 'no_errors' && !isCorrect) {
      return;
    }
    if (settings.mode === 'set_count' && newStats.total >= (settings.problemCount || 10)) {
      setTimeout(() => {
        setShowFeedback(false);
        endGame(newStats);
      }, isCorrect ? 1000 : 0);
      return;
    }
    if (isCorrect) {
      setTimeout(() => {
        setShowFeedback(false);
        setCurrentProblem(generateProblem(settings.problemTypes, settings.advancedOptions));
        setIsTransitioning(false);
      }, 1000);
    }
  };
  const handleContinueAfterIncorrect = () => {
    if (!settings) return;
    setShowFeedback(false);
    if (settings.mode === 'no_errors') {
      endGame(stats);
      return;
    }
    setCurrentProblem(generateProblem(settings.problemTypes, settings.advancedOptions));
    setIsTransitioning(false);
  };
  const endGame = (finalStats: GameStats) => {
    setStats({
      ...finalStats,
      endTime: Date.now()
    });
    setAppState('results');
    setIsTransitioning(false);
  };
  const handleQuit = () => {
    setAppState('menu');
    setSettings(null);
  };
  const handleRestart = () => {
    if (settings) {
      startGame(settings);
    }
  };
  const handleBack = () => {
    if (appState === 'menu') {
      setAppState('type_selection');
    } else if (appState === 'results') {
      setAppState('type_selection');
    } else if (appState === 'high_scores') {
      setAppState('type_selection');
    }
  };
  return <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-indigo-100 flex flex-col" data-id="element-0">
      {/* Global Header - Full Width */}
      {appState !== 'playing' && <header className="bg-white border-b border-slate-200 px-4 py-4 flex items-center justify-center relative w-full" data-id="element-1">
          {(appState === 'menu' || appState === 'results' || appState === 'high_scores') && <button onClick={handleBack} className="absolute left-4 p-2 text-slate-500 hover:text-slate-900 transition-colors" data-id="element-2">
              <ChevronLeft className="w-6 h-6" data-id="element-3" />
            </button>}
          <h1 className="text-xl font-bold text-slate-900" data-id="element-4">AXIOMATH</h1>

          {/* High Scores Icon - Only on type_selection and menu */}
          {(appState === 'type_selection' || appState === 'menu') && <button onClick={() => setAppState('high_scores')} className="absolute right-4 p-2 text-yellow-600 hover:text-yellow-700 transition-colors" data-id="element-5">
              <Trophy className="w-6 h-6" data-id="element-6" />
            </button>}
        </header>}

      {/* Playing Header - Different Style */}
      {appState === 'playing' && settings && <header className="p-4 bg-white border-b border-slate-100 w-full" data-id="element-7">
          <div className="max-w-4xl mx-auto flex items-center justify-between relative" data-id="element-8">
            {/* Left: Quit Button */}
            <div className="flex items-center" data-id="element-9">
              <Button variant="ghost" size="sm" onClick={handleQuit} className="text-slate-400 hover:text-slate-600" data-id="element-10">
                <X className="w-5 h-5" data-id="element-11" />
              </Button>
            </div>

            {/* Center: Timer */}
            <div className="absolute left-1/2 transform -translate-x-1/2" data-id="element-12">
              <Timer isRunning={true} startTime={stats.startTime} data-id="element-13" />
            </div>

            {/* Right: Streak and Progress */}
            <div className="flex items-center gap-4" data-id="element-14">
              <div className="text-sm font-medium text-slate-600" data-id="element-15">
                Streak:{' '}
                <span className="text-indigo-600 font-bold" data-id="element-16">
                  {stats.streak}
                </span>
              </div>
              {settings.mode === 'set_count' && <div className="w-32 hidden sm:block" data-id="element-17">
                  <ProgressBar current={stats.total} total={settings.problemCount || 10} data-id="element-18" />
                </div>}
            </div>
          </div>
        </header>}

      {/* Feedback Overlay */}
      <FeedbackOverlay show={showFeedback} isCorrect={lastAnswerCorrect} correctAnswer={lastCorrectAnswer} question={currentProblem?.question} onContinue={handleContinueAfterIncorrect} data-id="element-19" />

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col items-center justify-center w-full overflow-auto" data-id="element-20">
        <AnimatePresence mode="wait" data-id="element-21">
          {appState === 'type_selection' && <motion.div key="type_selection" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="w-full h-full" data-id="element-22">
              <ProblemTypeSelector onContinue={handleTypeSelection} initialSelected={selectedTypes} data-id="element-23" />
            </motion.div>}

          {appState === 'menu' && <motion.div key="menu" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="w-full h-full" data-id="element-24">
              <ModeSelector onStart={startGame} selectedProblemTypes={selectedTypes} data-id="element-25" />
            </motion.div>}

          {appState === 'playing' && currentProblem && settings && <motion.div key="game" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="w-full max-w-2xl space-y-8 p-4" data-id="element-26">
              <ProblemDisplay problem={currentProblem} data-id="element-27" />

              <div className="mt-8" data-id="element-28">
                {settings.inputType === 'multiple_choice' ? <MultipleChoice key={currentProblem.id} problem={currentProblem} onAnswer={handleAnswer} disabled={isTransitioning} data-id="element-29" /> : <FillInBlank key={currentProblem.id} onAnswer={handleAnswer} disabled={isTransitioning} data-id="element-30" />}
              </div>
            </motion.div>}

          {appState === 'results' && settings && <motion.div key="results" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 1.05
        }} className="w-full h-full" data-id="element-31">
              <Results stats={stats} mode={settings.mode} onRestart={handleRestart} onHome={() => setAppState('type_selection')} data-id="element-32" />
            </motion.div>}

          {appState === 'high_scores' && <motion.div key="high_scores" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 1.05
        }} className="w-full h-full" data-id="element-33">
              <HighScores data-id="element-34" />
            </motion.div>}
        </AnimatePresence>
      </main>
    </div>;
}